// Section layout & composition controls (Tier 3).
//
// Until these existed, `Section`'s entire visual surface was a `BlockStyle`
// and every section rendered as a fixed-width column in one vertical stack —
// which is the single biggest reason stock output reads as templated. These
// six primitives are what page builders actually differentiate on:
//
//   • width          — full-bleed / breakout vs the content column
//   • min-height     — turns any section into a hero-quality canvas
//   • shape dividers — wave / slant / curve transitions between bands
//   • decorations    — blobs, glows, dot grids that fill negative space
//   • sticky         — pin a section while it is in view
//   • anchor         — make the section a `scroll_to` / `#hash` target
//
// All of them are CSS-only and degrade to a plain section, so nothing here
// can break a page that stops supporting it.

import { useState } from 'react'
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react'
import type {
  Decoration, DecorationCorner, DecorationKind, Section, SectionWidth,
  ShapeDivider, ShapeDividerKind,
} from './types'
import ColorValueRow from './ColorValueRow'

interface Props {
  section: Section
  onChange: (patch: Partial<Section>) => void
}

const DIVIDER_KINDS: ShapeDividerKind[] = [
  'none', 'wave', 'slant', 'curve', 'triangle', 'arrow', 'zigzag', 'book',
]
const DECORATION_KINDS: DecorationKind[] = [
  'blob', 'glow', 'dot-grid', 'grid-lines', 'rings', 'noise',
]
const CORNERS: DecorationCorner[] = [
  'top-left', 'top-right', 'bottom-left', 'bottom-right', 'center',
]

export default function SectionLayoutEditor({ section, onChange }: Props) {
  const [open, setOpen] = useState(
    !!(section.width || section.minHeight || section.dividerTop || section.dividerBottom
      || section.decorations?.length || section.sticky || section.anchorId),
  )

  const setDivider = (edge: 'dividerTop' | 'dividerBottom', patch: Partial<ShapeDivider>) => {
    const current = section[edge] || { kind: 'none' as ShapeDividerKind }
    const next = { ...current, ...patch }
    onChange({ [edge]: next.kind === 'none' ? undefined : next } as Partial<Section>)
  }

  const setDecoration = (id: string, patch: Partial<Decoration>) => {
    onChange({
      decorations: (section.decorations || []).map((d) => (d.id === id ? { ...d, ...patch } : d)),
    })
  }
  const addDecoration = () => {
    onChange({
      decorations: [
        ...(section.decorations || []),
        {
          id: `deco_${Math.random().toString(36).slice(2, 8)}`,
          kind: 'blob', corner: 'top-right', size: 320, opacity: 0.16,
        },
      ],
    })
  }
  const removeDecoration = (id: string) => {
    const next = (section.decorations || []).filter((d) => d.id !== id)
    onChange({ decorations: next.length ? next : undefined })
  }

  return (
    <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 10 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, width: '100%',
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--color-text)', fontSize: '0.8425rem', fontWeight: 600,
        }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span style={LABEL}>Layout &amp; composition</span>
      </button>

      {open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 10 }}>
          {/* ── Width ── */}
          <div>
            <label style={MINI}>Width</label>
            <select
              value={section.width || 'contained'}
              onChange={(e) => onChange({ width: (e.target.value || undefined) as SectionWidth })}
              style={INPUT}
            >
              <option value="contained">Contained (page column)</option>
              <option value="full">Full bleed (edge to edge)</option>
              <option value="breakout">Breakout (wider than the column)</option>
            </select>
            <p style={HINT}>
              Full bleed lets the section's background reach the viewport edges
              while its content stays on the same measure as every other
              section — the standard marketing "band".
            </p>
          </div>

          {/* ── Height ── */}
          <div style={ROW}>
            <div style={{ flex: 1 }}>
              <label style={MINI}>Min height</label>
              <select
                value={section.minHeight === 'screen' ? 'screen' : (section.minHeight ? 'px' : '')}
                onChange={(e) => {
                  const v = e.target.value
                  onChange({ minHeight: v === 'screen' ? 'screen' : v === 'px' ? 480 : undefined })
                }}
                style={INPUT}
              >
                <option value="">Auto (content)</option>
                <option value="screen">Full screen</option>
                <option value="px">Fixed px…</option>
              </select>
            </div>
            {typeof section.minHeight === 'number' && (
              <div style={{ flex: 1 }}>
                <label style={MINI}>Height (px)</label>
                <input
                  type="number" min={80} max={2000}
                  value={section.minHeight}
                  onChange={(e) => onChange({ minHeight: Number(e.target.value) || undefined })}
                  style={INPUT}
                />
              </div>
            )}
            {section.minHeight && (
              <div style={{ flex: 1 }}>
                <label style={MINI}>Content</label>
                <select
                  value={section.verticalAlign || 'center'}
                  onChange={(e) => onChange({ verticalAlign: e.target.value as 'top' | 'center' | 'bottom' })}
                  style={INPUT}
                >
                  <option value="top">Top</option>
                  <option value="center">Center</option>
                  <option value="bottom">Bottom</option>
                </select>
              </div>
            )}
          </div>

          {/* ── Anchor ── */}
          <div>
            <label style={MINI}>Anchor id</label>
            <input
              value={section.anchorId || ''}
              onChange={(e) => onChange({
                // Anchor ids land in a URL fragment, so normalise to something
                // that is actually valid there rather than trusting free text.
                anchorId: e.target.value.trim().toLowerCase().replace(/[^a-z0-9-_]+/g, '-').replace(/^-+|-+$/g, '') || undefined,
              })}
              placeholder="e.g. pricing"
              style={{ ...INPUT, fontFamily: 'monospace' }}
            />
            <p style={HINT}>
              Makes this section a target for "Scroll to section" buttons and
              <code> #hash</code> links — how a one-page site navigates.
            </p>
          </div>

          {/* ── Sticky ── */}
          <div style={ROW}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', flex: 1 }}>
              <input
                type="checkbox"
                checked={!!section.sticky}
                onChange={(e) => onChange({ sticky: e.target.checked || undefined })}
              />
              Pin while in view
            </label>
            {section.sticky && (
              <div style={{ flex: 1 }}>
                <label style={MINI}>Top offset (px)</label>
                <input
                  type="number" min={0} max={300}
                  value={section.stickyOffset ?? ''}
                  onChange={(e) => onChange({ stickyOffset: Number(e.target.value) || undefined })}
                  placeholder="0"
                  style={INPUT}
                />
              </div>
            )}
          </div>

          {/* ── Shape dividers ── */}
          <div>
            <label style={LABEL}>Shape dividers</label>
            {(['dividerTop', 'dividerBottom'] as const).map((edge) => {
              const d = section[edge]
              return (
                <div key={edge} style={{ marginTop: 8 }}>
                  <label style={MINI}>{edge === 'dividerTop' ? 'Top edge' : 'Bottom edge'}</label>
                  <div style={ROW}>
                    <select
                      value={d?.kind || 'none'}
                      onChange={(e) => setDivider(edge, { kind: e.target.value as ShapeDividerKind })}
                      style={{ ...INPUT, flex: 2 }}
                    >
                      {DIVIDER_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                    </select>
                    {d && d.kind !== 'none' && (
                      <input
                        type="number" min={10} max={300}
                        value={d.height ?? 60}
                        onChange={(e) => setDivider(edge, { height: Number(e.target.value) || 60 })}
                        title="Height (px)"
                        style={{ ...INPUT, flex: 1 }}
                      />
                    )}
                  </div>
                  {d && d.kind !== 'none' && (
                    <div style={{ ...ROW, marginTop: 6 }}>
                      <div style={{ flex: 2 }}>
                        <ColorValueRow
                          value={d.color || ''}
                          onChange={(c) => setDivider(edge, { color: c || undefined })}
                          placeholder="next section's color"
                        />
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={!!d.flipX}
                          onChange={(e) => setDivider(edge, { flipX: e.target.checked || undefined })}
                        />
                        Flip
                      </label>
                      <label
                        style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        title="Draw the divider on top of the section's content instead of behind it"
                      >
                        <input
                          type="checkbox"
                          checked={!!d.above}
                          onChange={(e) => setDivider(edge, { above: e.target.checked || undefined })}
                        />
                        Draw over content
                      </label>
                    </div>
                  )}
                </div>
              )
            })}
            <p style={HINT}>
              Set the divider colour to the colour of the section it flows
              INTO — that's what makes the two bands read as one shape.
            </p>
          </div>

          {/* ── Decorations ── */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <label style={{ ...LABEL, marginBottom: 0 }}>Decorations</label>
              <button
                onClick={addDecoration}
                className="btn btn-ghost btn-sm"
                style={{ marginLeft: 'auto' }}
                title="Add a decorative layer"
              >
                <Plus size={13} /> Add
              </button>
            </div>
            {(section.decorations || []).map((d) => (
              <div key={d.id} style={{
                marginTop: 8, padding: 8, borderRadius: 6,
                border: '1px solid var(--color-border)',
                display: 'flex', flexDirection: 'column', gap: 6,
              }}>
                <div style={ROW}>
                  <select
                    value={d.kind}
                    onChange={(e) => setDecoration(d.id, { kind: e.target.value as DecorationKind })}
                    style={{ ...INPUT, flex: 1 }}
                  >
                    {DECORATION_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                  <select
                    value={d.corner || 'top-right'}
                    onChange={(e) => setDecoration(d.id, { corner: e.target.value as DecorationCorner })}
                    style={{ ...INPUT, flex: 1 }}
                  >
                    {CORNERS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button
                    onClick={() => removeDecoration(d.id)}
                    className="btn btn-ghost btn-sm"
                    style={{ padding: 4, color: 'var(--color-error)' }}
                    title="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                <div style={ROW}>
                  <input
                    type="number" min={60} max={1200}
                    value={d.size ?? 320}
                    onChange={(e) => setDecoration(d.id, { size: Number(e.target.value) || undefined })}
                    title="Size (px)"
                    style={{ ...INPUT, flex: 1 }}
                  />
                  <input
                    type="number" min={0} max={1} step={0.02}
                    value={d.opacity ?? 0.16}
                    onChange={(e) => setDecoration(d.id, { opacity: Number(e.target.value) })}
                    title="Opacity"
                    style={{ ...INPUT, flex: 1 }}
                  />
                  <div style={{ flex: 2 }}>
                    <ColorValueRow
                      value={d.color || ''}
                      onChange={(c) => setDecoration(d.id, { color: c || undefined })}
                      placeholder="primary"
                    />
                  </div>
                </div>
                <div style={ROW}>
                  {/* Blur only paints on the soft shapes (blob / glow); a saved
                      value on another kind is still shown so it can be cleared. */}
                  {(d.kind === 'blob' || d.kind === 'glow' || typeof d.blur === 'number') && (
                    <div style={{ flex: 1 }}>
                      <label style={MINI}>Blur (px)</label>
                      <input
                        type="number" min={0} max={300}
                        value={d.blur ?? ''}
                        onChange={(e) => setDecoration(d.id, { blur: numOrUndef(e.target.value) })}
                        placeholder={d.kind === 'glow' ? '90' : d.kind === 'blob' ? '60' : 'n/a'}
                        style={INPUT}
                      />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <label style={MINI}>Offset X (px)</label>
                    <input
                      type="number" min={-600} max={600}
                      value={d.offsetX ?? ''}
                      onChange={(e) => setDecoration(d.id, { offsetX: numOrUndef(e.target.value) })}
                      placeholder="0"
                      style={INPUT}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={MINI}>Offset Y (px)</label>
                    <input
                      type="number" min={-600} max={600}
                      value={d.offsetY ?? ''}
                      onChange={(e) => setDecoration(d.id, { offsetY: numOrUndef(e.target.value) })}
                      placeholder="0"
                      style={INPUT}
                    />
                  </div>
                </div>
              </div>
            ))}
            {!(section.decorations || []).length && (
              <p style={HINT}>
                Blobs, glows and dot grids fill negative space. They sit behind
                the content and never intercept clicks.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

/** Parse a numeric input, treating an empty field as "unset". */
function numOrUndef(v: string): number | undefined {
  if (v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

const LABEL: React.CSSProperties = {
  fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--color-text-secondary)',
  marginBottom: 4, display: 'block',
}
const MINI: React.CSSProperties = {
  fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
  marginBottom: 4, display: 'block',
}
const INPUT: React.CSSProperties = {
  width: '100%', padding: '6px 10px', fontSize: '0.9125rem',
  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 6, color: 'var(--color-text)', outline: 'none',
}
const ROW: React.CSSProperties = { display: 'flex', gap: 6, alignItems: 'flex-end' }
const HINT: React.CSSProperties = {
  margin: '6px 0 0', fontSize: '0.8125rem', lineHeight: 1.45,
  color: 'var(--color-text-secondary)',
}
